import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  Manager360Response,
  ManagerAction,
  ManagerActionPayload,
  ManagerDueDiligence,
  ManagerDueDiligenceDetail,
  ManagerDueDiligencePayload,
  ManagerDueDiligenceWorkbenchParams,
  ManagerDueDiligenceWorkbenchResponse,
  ManagerEvidence,
  ManagerEvidencePayload,
  UploadManagerEvidencePayload,
  ManagerInteraction,
  ManagerInteractionPayload,
  ManagerInvite,
  ManagerOperationLogList,
  PublicManagerInvite,
  ManagerInstitutionData,
  ManagerLicensePayload,
  ManagerContactPayload,
  ManagerOwnershipPayload,
  ManagerAumPayload,
  ManagerRelationPayload,
  ManagerPerformanceSnapshot,
  ManagerStyleSnapshot,
  ManagerProfilePayload,
  ManagerReview,
  ManagerReviewPayload,
  ManagerRiskEvent,
  ManagerRiskAlert,
  ManagerRiskAlertEvaluationResult,
  ManagerRiskAlertsDashboard,
  ManagerRiskAlertRules,
  ManagerRiskAlertStatus,
  UpdateManagerRiskAlertRulesPayload,
  ManagerStrategy,
  ManagerStrategyPayload,
  ManagerTeamMember,
  ManagerTeamMemberPayload,
  ManagerWorkbenchParams,
  ManagerWorkbenchResponse,
} from './types/manager-research';

const request = createAppRequest();

export async function getManagerInviteFile(
  managerId: number,
  inviteId: number,
  fileId: number,
  options?: ApiRequestOptions,
) {
  return request<Blob>(
    `/bff/fams/managers/${managerId}/invites/${inviteId}/files/${fileId}`,
    { method: 'GET', responseType: 'blob', ...(options || {}) },
  );
}

export async function getManagerEvidenceFile(
  managerId: number,
  evidenceId: number,
  options?: ApiRequestOptions,
) {
  return request<Blob>(
    `/bff/fams/managers/${managerId}/evidence/${evidenceId}/file`,
    { method: 'GET', responseType: 'blob', ...(options || {}) },
  );
}

export async function getManagerWorkbench(params?: ManagerWorkbenchParams, options?: ApiRequestOptions) {
  return request<ManagerWorkbenchResponse>('/bff/fams/managers/workbench', { method: 'GET', params, ...(options || {}) });
}

export async function getManager360(id: number, options?: ApiRequestOptions) {
  return request<Manager360Response>(`/bff/fams/managers/${id}/360`, { method: 'GET', ...(options || {}) });
}

export async function getManagerInstitution(id: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/institution`, { method: 'GET', ...(options || {}) });
}

export async function createManagerType(id: number, data: { typeCode: string }, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/types`, { method: 'POST', data, ...(options || {}) });
}
export async function deleteManagerType(id: number, typeId: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/types/${typeId}`, { method: 'DELETE', ...(options || {}) });
}
export async function createManagerLicense(id: number, data: ManagerLicensePayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/licenses`, { method: 'POST', data, ...(options || {}) });
}
export async function updateManagerLicense(id: number, itemId: number, data: ManagerLicensePayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/licenses/${itemId}`, { method: 'PUT', data, ...(options || {}) });
}
export async function deleteManagerLicense(id: number, itemId: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/licenses/${itemId}`, { method: 'DELETE', ...(options || {}) });
}
export async function createManagerContact(id: number, data: ManagerContactPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/contacts`, { method: 'POST', data, ...(options || {}) });
}
export async function updateManagerContact(id: number, itemId: number, data: ManagerContactPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/contacts/${itemId}`, { method: 'PUT', data, ...(options || {}) });
}
export async function deleteManagerContact(id: number, itemId: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/contacts/${itemId}`, { method: 'DELETE', ...(options || {}) });
}
export async function createManagerOwnership(id: number, data: ManagerOwnershipPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/ownership`, { method: 'POST', data, ...(options || {}) });
}
export async function updateManagerOwnership(id: number, itemId: number, data: ManagerOwnershipPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/ownership/${itemId}`, { method: 'PUT', data, ...(options || {}) });
}
export async function deleteManagerOwnership(id: number, itemId: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/ownership/${itemId}`, { method: 'DELETE', ...(options || {}) });
}
export async function createManagerAumSnapshot(id: number, data: ManagerAumPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/aum-history`, { method: 'POST', data, ...(options || {}) });
}
export async function updateManagerAumSnapshot(id: number, itemId: number, data: ManagerAumPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/aum-history/${itemId}`, { method: 'PUT', data, ...(options || {}) });
}
export async function deleteManagerAumSnapshot(id: number, itemId: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/aum-history/${itemId}`, { method: 'DELETE', ...(options || {}) });
}
export async function createManagerRelation(id: number, data: ManagerRelationPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/relations`, { method: 'POST', data, ...(options || {}) });
}
export async function updateManagerRelation(id: number, itemId: number, data: ManagerRelationPayload, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/relations/${itemId}`, { method: 'PUT', data, ...(options || {}) });
}
export async function deleteManagerRelation(id: number, itemId: number, options?: ApiRequestOptions) {
  return request<ManagerInstitutionData>(`/bff/fams/managers/${id}/relations/${itemId}`, { method: 'DELETE', ...(options || {}) });
}

export async function updateManagerProfile(id: number, data: ManagerProfilePayload, options?: ApiRequestOptions) {
  return request<Manager360Response['profile']>(`/bff/fams/managers/${id}/profile`, { method: 'PUT', data, ...(options || {}) });
}

export async function updateManagerLifecycle(id: number, state: ManagerProfilePayload['lifecycleState'], reason?: string, options?: ApiRequestOptions) {
  return request<Manager360Response['profile']>(`/bff/fams/managers/${id}/lifecycle`, { method: 'PATCH', data: { state, reason }, ...(options || {}) });
}

export async function getManagerOperationLogs(
  id: number,
  params?: { current?: number; pageSize?: number },
  options?: ApiRequestOptions,
) {
  return request<ManagerOperationLogList>(
    `/bff/fams/managers/${id}/operation-logs`,
    { method: 'GET', params, ...(options || {}) },
  );
}

export async function createManagerStrategy(id: number, data: ManagerStrategyPayload, options?: ApiRequestOptions) {
  return request<ManagerStrategy>(`/bff/fams/managers/${id}/strategies`, { method: 'POST', data, ...(options || {}) });
}

export async function createManagerTeamMember(id: number, data: ManagerTeamMemberPayload, options?: ApiRequestOptions) {
  return request<ManagerTeamMember>(`/bff/fams/managers/${id}/team`, { method: 'POST', data, ...(options || {}) });
}

export async function createManagerInteraction(id: number, data: ManagerInteractionPayload, options?: ApiRequestOptions) {
  return request<ManagerInteraction>(`/bff/fams/managers/${id}/interactions`, { method: 'POST', data, ...(options || {}) });
}

export async function createManagerInvite(
  id: number,
  data: {
    interactionId?: number;
    requestMessage?: string;
    requestedTypes?: string[];
    expiresInDays?: number;
  },
  options?: ApiRequestOptions,
) {
  return request<ManagerInvite>(`/bff/fams/managers/${id}/invites`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getManagerInvite(
  id: number,
  inviteId: number,
  options?: ApiRequestOptions,
) {
  return request<ManagerInvite>(`/bff/fams/managers/${id}/invites/${inviteId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function regenerateManagerInvite(
  id: number,
  inviteId: number,
  options?: ApiRequestOptions,
) {
  return request<ManagerInvite>(
    `/bff/fams/managers/${id}/invites/${inviteId}/regenerate`,
    { method: 'POST', ...(options || {}) },
  );
}

export async function revokeManagerInvite(
  id: number,
  inviteId: number,
  options?: ApiRequestOptions,
) {
  return request<ManagerInvite>(
    `/bff/fams/managers/${id}/invites/${inviteId}/revoke`,
    { method: 'POST', ...(options || {}) },
  );
}

export async function reviewManagerInvite(
  id: number,
  inviteId: number,
  data: { decision: 'APPROVED' | 'REJECTED'; comment?: string },
  options?: ApiRequestOptions,
) {
  return request<ManagerInvite>(
    `/bff/fams/managers/${id}/invites/${inviteId}/review`,
    { method: 'POST', data, ...(options || {}) },
  );
}

export async function getPublicManagerInvite(token: string, options?: ApiRequestOptions) {
  return request<PublicManagerInvite>(`/bff/public/manager-invites/${token}`, {
    method: 'GET',
    skipErrorHandler: true,
    ...(options || {}),
  });
}

export async function uploadPublicManagerInviteFile(
  token: string,
  file: File,
  options?: ApiRequestOptions,
) {
  const form = new FormData();
  form.append('file', file);
  return request<{ id: number; fileName: string; fileSize: number }>(
    `/bff/public/manager-invites/${token}/files`,
    { method: 'POST', data: form, skipErrorHandler: true, ...(options || {}) },
  );
}

export async function deletePublicManagerInviteFile(
  token: string,
  fileId: number,
  options?: ApiRequestOptions,
) {
  return request(`/bff/public/manager-invites/${token}/files/${fileId}`, {
    method: 'DELETE',
    skipErrorHandler: true,
    ...(options || {}),
  });
}

export async function submitPublicManagerInvite(
  token: string,
  data: {
    submitterName: string;
    submitterContact?: string;
    submitNote?: string;
  },
  options?: ApiRequestOptions,
) {
  return request<PublicManagerInvite>(`/bff/public/manager-invites/${token}/submit`, {
    method: 'POST',
    data,
    skipErrorHandler: true,
    ...(options || {}),
  });
}

export async function createManagerDueDiligence(id: number, data: ManagerDueDiligencePayload, options?: ApiRequestOptions) {
  return request<ManagerDueDiligence>(`/bff/fams/managers/${id}/due-diligence`, { method: 'POST', data, ...(options || {}) });
}

export async function getDueDiligenceWorkbench(
  params?: ManagerDueDiligenceWorkbenchParams,
  options?: ApiRequestOptions,
) {
  return request<ManagerDueDiligenceWorkbenchResponse>('/bff/fams/due-diligence', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getDueDiligenceDetail(id: number, options?: ApiRequestOptions) {
  return request<ManagerDueDiligenceDetail>(`/bff/fams/due-diligence/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createManagerEvidence(id: number, data: ManagerEvidencePayload, options?: ApiRequestOptions) {
  return request<ManagerEvidence>(`/bff/fams/managers/${id}/evidence`, { method: 'POST', data, ...(options || {}) });
}

export async function uploadManagerEvidence(
  id: number,
  data: UploadManagerEvidencePayload,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('file', data.file);
  if (data.evidenceType) formData.append('evidenceType', data.evidenceType);
  if (data.title) formData.append('title', data.title);
  if (data.source) formData.append('source', data.source);
  if (data.version) formData.append('version', data.version);
  if (data.publishedDate) formData.append('publishedDate', data.publishedDate);
  if (data.confidentiality)
    formData.append('confidentiality', data.confidentiality);
  if (data.remark) formData.append('remark', data.remark);
  if (data.strategyIds?.length)
    formData.append('strategyIds', JSON.stringify(data.strategyIds));
  if (data.uploaderName) formData.append('uploaderName', data.uploaderName);
  return request<ManagerEvidence>(`/bff/fams/managers/${id}/evidence/upload`, {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
}

export async function archiveManagerEvidence(
  id: number,
  evidenceId: number,
  options?: ApiRequestOptions,
) {
  return request<{ success: boolean; id: number }>(
    `/bff/fams/managers/${id}/evidence/${evidenceId}/archive`,
    { method: 'PATCH', ...(options || {}) },
  );
}

export async function createManagerReview(id: number, data: ManagerReviewPayload, options?: ApiRequestOptions) {
  return request<ManagerReview>(`/bff/fams/managers/${id}/reviews`, { method: 'POST', data, ...(options || {}) });
}

export async function createManagerAction(id: number, data: ManagerActionPayload, options?: ApiRequestOptions) {
  return request<ManagerAction>(`/bff/fams/managers/${id}/actions`, { method: 'POST', data, ...(options || {}) });
}

export async function updateManagerAction(id: number, actionId: number, data: ManagerActionPayload, options?: ApiRequestOptions) {
  return request<ManagerAction>(`/bff/fams/managers/${id}/actions/${actionId}`, { method: 'PUT', data, ...(options || {}) });
}

export async function getManagerRiskEvents(id?: number, options?: ApiRequestOptions) {
  return request<ManagerRiskEvent[]>(id ? `/bff/fams/managers/${id}/risk-events` : '/bff/fams/managers/alerts', { method: 'GET', ...(options || {}) });
}

export async function getManagerRiskAlertsDashboard(
  params?: {
    category?: string;
    severity?: string;
    status?: string;
    keyword?: string;
    trendDays?: number;
  },
  options?: ApiRequestOptions,
) {
  return request<ManagerRiskAlertsDashboard>('/bff/fams/manager-risk-alerts', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function updateManagerRiskAlertStatus(
  id: number,
  status: Exclude<ManagerRiskAlertStatus, 'unread'>,
  options?: ApiRequestOptions,
) {
  return request<ManagerRiskAlert>(`/bff/fams/manager-risk-alerts/${id}/status`, {
    method: 'PATCH',
    data: { status },
    ...(options || {}),
  });
}

export async function markAllManagerRiskAlertsRead(options?: ApiRequestOptions) {
  return request<{ success: boolean; affected: number }>(
    '/bff/fams/manager-risk-alerts/read-all',
    { method: 'PATCH', ...(options || {}) },
  );
}

export async function updateManagerRiskAlertRules(
  data: UpdateManagerRiskAlertRulesPayload,
  options?: ApiRequestOptions,
) {
  return request<ManagerRiskAlertRules>('/bff/fams/manager-risk-alerts/rules', {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function evaluateManagerRiskAlerts(options?: ApiRequestOptions) {
  return request<ManagerRiskAlertEvaluationResult>('/bff/fams/manager-risk-alerts/evaluate', {
    method: 'POST',
    ...(options || {}),
  });
}

export async function getManagerPerformance(id: number, options?: ApiRequestOptions) {
  return request<ManagerPerformanceSnapshot[]>(`/bff/fams/managers/${id}/performance`, { method: 'GET', ...(options || {}) });
}

export async function getManagerStyle(id: number, options?: ApiRequestOptions) {
  return request<ManagerStyleSnapshot[]>(`/bff/fams/managers/${id}/style`, { method: 'GET', ...(options || {}) });
}

export async function recalculateManager(id: number, options?: ApiRequestOptions) {
  return request<{ dataCompleteness: number; productCount: number; marketValue: number; openRiskCount: number }>(`/bff/fams/managers/${id}/recalculate`, { method: 'POST', ...(options || {}) });
}
